import { searchAirports } from '@/src/api/requests/search-airports'
import { PopularAirportsResponse, useGetPopularAirportsQuery } from '@/src/api/rtkQuery/popularAirport'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import { getBrandName } from '@/src/utils/buildUtils'
import { getFormattedDate, isValidSearchQueryDate } from '@/src/utils/date'
import { useLocale } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

// Interface for date range selection (departure and return dates)
export interface IRange {
    from: Date
    to: Date
    enteredTo: Date
}

// Interface for traveler count configuration
export interface ITravelers {
    adults: number
    childs: number
    infants: number
}

// Interface for airport input state (used in fromToInputsState and multi-city segments)
export interface AirportInputState {
    value: string
    city: string
    options: any[]
    loading: boolean
    error: any
    airportCode: string
    isPopularAirports: boolean
}

// Interface for multi-city segment
export interface MultiCitySegment {
    from: AirportInputState
    to: AirportInputState
    date: Date | null
}

/**
 * Custom hook to manage flight search form state and operations
 * Handles airport selection, date ranges, traveler counts, and flight class
 * @param jsons - Optional JSON data
 * @param srpResultsLoading - Optional loading state for SRP results
 * @param recentSearch - Optional recent search data
 * @param maxSegments - Maximum number of segments allowed (default: 6 for desktop, 3 for mobile)
 * @returns Object containing all state and handler functions
 */
export const useSearchInputManager = (
    jsons?: Record<string, any>,
    srpResultsLoading?: boolean,
    recentSearch?: {
        from: string
        to: string
        origin: string
        destination: string
        depart_date: string
        return_date: string
    },
    maxSegments: number = 6
) => {
    const { urlQueryObj, isSeoQuery } = useGetSearchParamsObj()

    const locale = useLocale()
    // Extract and set default values from query parameters
    const {
        from = '',
        to = '',
        adults = '1',
        childs = '0',
        infants = '0',
        class: flightClass = 'Economy',
        depart_date = '',
        origin = '',
        destination = '',
        return_date = ''
    } = urlQueryObj || {}

    const initialDepartDate = recentSearch?.depart_date || (depart_date as string)
    const initialReturnDate = recentSearch?.depart_date ? recentSearch?.return_date || '' : (return_date as string)

    // ===== AIRPORT SELECTION STATE =====
    /**
     * State for managing airport search inputs (from/to destinations)
     * Includes value, search options, loading state, and error handling
     */
    const brandName = getBrandName()
    const { data: popularAirportData } = useGetPopularAirportsQuery(brandName)

    const popularAirportList = useMemo(() => popularAirportData?.[locale as keyof PopularAirportsResponse] || [], [popularAirportData, locale])
    const [fromToInputsState, setFromToInputsState] = useState({
        from: {
            value: recentSearch?.origin || recentSearch?.from || from || '',
            city: '',
            options: popularAirportList,
            loading: false,
            error: null,
            airportCode: recentSearch?.from || from || '',
            isPopularAirports: true
        },
        to: {
            value: recentSearch?.destination || recentSearch?.to || to || '',
            city: '',
            airportCode: recentSearch?.to || to || '',
            options: popularAirportList,
            loading: false,
            error: null,
            isPopularAirports: true
        }
    })

    /**
     * Fetches airport suggestions based on user input
     * @param query - Search query string
     * @param action - Whether searching for 'from' or 'to' airport
     */
    const getfromData = async () => {
        const data = jsons?.ap
        const c = data?.[from as string]?.c || ''
        const cc = data?.[from as string]?.cc || ''

        setFromToInputsState(prev => ({
            ...prev,
            from: {
                ...prev.from,
                value: `${from}${c ? ` - ${c}` : ''}${cc ? `, ${cc}` : ''}`
            }
        }))
    }
    const gettoData = async () => {
        const data = jsons?.ap
        const c = data?.[to as string]?.c || ''
        const cc = data?.[to as string]?.cc || ''
        setFromToInputsState(prev => ({
            ...prev,
            to: {
                ...prev.to,
                value: `${to}${c ? ` - ${c}` : ''}${cc ? `, ${cc}` : ''}`
            }
        }))
    }

    // ===== DATE RANGE STATE =====
    // Initialize date range with query parameters or current date
    function getRangeFromQuery (depart_date: string, return_date: string): IRange {
        const isValidDepartDate = isValidSearchQueryDate(depart_date)
        const isValidReturnDate = isValidSearchQueryDate(return_date)

        const fromDate = isValidDepartDate ? new Date(getFormattedDate(depart_date, 'MM/DD/YYYY')) : new Date()

        const toDate = isValidReturnDate ? new Date(getFormattedDate(return_date, 'MM/DD/YYYY')) : fromDate

        return {
            from: fromDate,
            to: toDate,
            enteredTo: toDate
        }
    }

    const [range, setRange] = useState<IRange>(() =>
        getRangeFromQuery(initialDepartDate as string, initialReturnDate as string)
    )

    useEffect(() => {
        if (!srpResultsLoading) {
            getfromData()
            gettoData()
        }
    }, [srpResultsLoading, from, to, jsons])

    useEffect(() => {
        if (recentSearch?.origin || recentSearch?.destination) {
            setFromToInputsState(prev => ({
                ...prev,
                from: {
                    ...prev.from,
                    value: recentSearch?.origin || recentSearch?.from || '',
                    airportCode: recentSearch?.from || ''
                },
                to: {
                    ...prev.to,
                    value: recentSearch?.destination || recentSearch?.to || '',
                    airportCode: recentSearch?.to || ''
                }
            }))

            // Also set dates from recent search if available
            if (recentSearch?.depart_date) {
                setRange(getRangeFromQuery(recentSearch?.depart_date || '', recentSearch?.return_date || ''))
            }
        }
    }, [])

    const getAirportsFromQuery = async (query: string, action: 'from' | 'to') => {
        if (query.length < 1) {
            setFromToInputsState(prev => ({
                ...prev,
                [action]: {
                    ...prev[action],
                    options: popularAirportList,
                    loading: false,
                    isPopularAirports: true
                }
            }))
            return
        }

        try {
            setFromToInputsState(prev => ({
                ...prev,
                [action]: {
                    ...prev[action],
                    loading: true,
                    error: null
                }
            }))
            const airports = (await searchAirports(query)) as unknown as any
            setFromToInputsState(prev => ({
                ...prev,
                [action]: {
                    ...prev[action],
                    options: airports?.data || [],
                    loading: false,
                    isPopularAirports: false
                }
            }))
        } catch (error) {
            console.error('Error fetching airports:', error)
            setFromToInputsState(prev => ({
                ...prev,
                [action]: {
                    ...prev[action],
                    loading: false,
                    error: error instanceof Error ? error.message : 'An error occurred',
                    isPopularAirports: false
                }
            }))
        }
    }

    /**
     * Handles airport selection from dropdown
     * Extracts airport code from selected value (format: "CODE - Airport Name")
     * @param value - Selected airport value
     * @param action - Whether selecting 'from' or 'to' airport
     */
    const onAirportSelect = (value: string, action: 'from' | 'to', city?: string) => {
        setFromToInputsState(prev => ({
            ...prev,
            [action]: { ...prev[action], value, airportCode: value.split(' - ')[0], city: city || '' }
        }))
    }

    /**
     * Swaps the from and to airport selections
     */
    const handleSwap = () => {
        setFromToInputsState(prev => ({
            from: {
                ...prev.to
            },
            to: {
                ...prev.from
            }
        }))
    }

    // ===== TRIP TYPE STATE =====
    // Determine if this is a round trip based on presence of return date
    const isValidDepartDate = isValidSearchQueryDate(initialDepartDate)
    const isValidReturnDate = isValidSearchQueryDate(initialReturnDate)
    const [isRoundTrip, setIsRoundTrip] = useState(isValidDepartDate && isValidReturnDate)

    /**
     * Updates the trip type (one-way vs round-trip)
     * @param roundTrip - Boolean indicating if round trip is selected
     */
    const isRoundTripDispatch = (roundTrip: boolean) => {
        setIsRoundTrip(roundTrip)
    }

    // ===== NON-STOP FLIGHTS STATE =====
    const [isNonStop, setIsNonStop] = useState(false)

    /**
     * Updates the non-stop flights preference
     * @param nonStop - Boolean indicating if non-stop flights are preferred
     */
    const handleNonStopChange = (nonStop: boolean) => {
        setIsNonStop(nonStop)
    }

    useEffect(() => {
        if (depart_date || return_date) {
            setRange(getRangeFromQuery(depart_date as string, return_date as string))
        }
    }, [depart_date, return_date])

    /**
     * Updates the date range with validation
     * Ensures 'to' date is never before 'from' date
     * @param newRange - New date range object
     */
    const rangeDispatch = (newRange: IRange) => {
        setRange(prev => ({
            ...prev,
            ...newRange,
            to: newRange.to || newRange.from || prev.from,
            enteredTo: newRange.enteredTo || newRange.from || prev.from
        }))
    }

    // ===== TRAVELER COUNT STATE =====
    /**
     * State for managing passenger counts (adults, children, infants)
     */
    const [travelersState, setTravelersState] = useState<ITravelers>({
        adults: Number(adults) || 1,
        childs: Number(childs) || 0,
        infants: Number(infants) || 0
    })

    // ===== FLIGHT CLASS STATE =====
    /**
     * State for managing selected flight class (Economy, Business, First, etc.)
     */
    const [flightClassState, setFlightClassState] = useState(flightClass || 'Economy')

    // ===== BUSINESS RULES VALIDATION =====
    /**
     * Validates if adults can be increased (max 9 total passengers)
     */
    const canIncreaseAdults = (currentTravelers: ITravelers) => {
        return currentTravelers.adults + currentTravelers.childs < 9
    }

    /**
     * Validates if children can be increased (max 9 total passengers)
     */
    const canIncreaseChildren = (currentTravelers: ITravelers) => {
        return currentTravelers.adults + currentTravelers.childs < 9
    }

    /**
     * Validates if infants can be increased (max 1 infant per adult)
     */
    const canIncreaseInfants = (currentTravelers: ITravelers) => {
        return currentTravelers.infants < currentTravelers.adults
    }

    /**
     * Validates if adults can be decreased (minimum 1 adult required)
     */
    const canDecreaseAdults = (currentTravelers: ITravelers) => {
        return currentTravelers.adults > 1
    }

    /**
     * Validates if children can be decreased (minimum 0 children)
     */
    const canDecreaseChildren = (currentTravelers: ITravelers) => {
        return currentTravelers.childs > 0
    }

    /**
     * Validates if infants can be decreased (minimum 0 infants)
     */
    const canDecreaseInfants = (currentTravelers: ITravelers) => {
        return currentTravelers.infants > 0
    }

    // ===== TRAVELER MANAGEMENT HANDLERS =====
    /**
     * Handles traveler count changes with business rule validation
     * Automatically adjusts infant count when adults are decreased
     * @param type - Type of traveler to modify ('adults', 'childs', 'infants')
     * @param delta - Change amount (+1 for increase, -1 for decrease)
     */
    const handleTravelerChange = (type: keyof ITravelers, delta: number) => {
        setTravelersState(prev => {
            const newTravelers = { ...prev }

            if (delta > 0) {
                // Increasing traveler count
                if (type === 'adults' && canIncreaseAdults(prev)) {
                    newTravelers.adults = prev.adults + 1
                } else if (type === 'childs' && canIncreaseChildren(prev)) {
                    newTravelers.childs = prev.childs + 1
                } else if (type === 'infants' && canIncreaseInfants(prev)) {
                    newTravelers.infants = prev.infants + 1
                }
            } else {
                // Decreasing traveler count
                if (type === 'adults' && canDecreaseAdults(prev)) {
                    newTravelers.adults = prev.adults - 1
                    // Adjust infants if they exceed new adult count
                    if (newTravelers.infants > newTravelers.adults) {
                        newTravelers.infants = newTravelers.adults
                    }
                } else if (type === 'childs' && canDecreaseChildren(prev)) {
                    newTravelers.childs = prev.childs - 1
                } else if (type === 'infants' && canDecreaseInfants(prev)) {
                    newTravelers.infants = prev.infants - 1
                }
            }

            return newTravelers
        })
    }

    /**
     * Updates the selected flight class
     * @param newClass - New flight class selection
     */
    const handleFlightClassChange = (newClass: string) => {
        setFlightClassState(newClass)
    }
    // ===== MULTI-CITY STATE =====
    /**
     * State for managing multi-city segments
     * Each segment contains departure, destination, and date
     */
    const [multiCitySegments, setMultiCitySegments] = useState<MultiCitySegment[]>([
        {
            from: {
                value: '',
                city: '',
                options: popularAirportList,
                loading: false,
                error: null,
                airportCode: '',
                isPopularAirports: true
            },
            to: {
                value: '',
                city: '',
                options: popularAirportList,
                loading: false,
                error: null,
                airportCode: '',
                isPopularAirports: true
            },
            date: new Date() // First segment defaults to today's date
        },
        {
            from: {
                value: '',
                city: '',
                options: popularAirportList,
                loading: false,
                error: null,
                airportCode: '',
                isPopularAirports: true
            },
            to: {
                value: '',
                city: '',
                options: popularAirportList,
                loading: false,
                error: null,
                airportCode: '',
                isPopularAirports: true
            },
            date: new Date()
        }
    ])

    /**
     * Updates a specific field in a multi-city segment
     * @param segmentIndex - Index of the segment to update (0-indexed)
     * @param field - Field to update ('from', 'to', or 'date')
     * @param value - New value for the field (string for airport, Date for date, or AirportInputState for full airport object)
     */
    const handleMultiCitySegmentUpdate = (
        segmentIndex: number,
        field: 'from' | 'to' | 'date',
        value: string | Date | null | AirportInputState
    ) => {
        setMultiCitySegments(prev =>
            prev.map((segment, index) => {
                if (index === segmentIndex) {
                    if (field === 'date') {
                        return {
                            ...segment,
                            date: value as Date | null
                        }
                    } else if (typeof value === 'string') {
                        // If value is a string, update the airport field's value and airportCode
                        const airportField = field === 'from' ? 'from' : 'to'
                        return {
                            ...segment,
                            [airportField]: {
                                ...segment[airportField],
                                value: value,
                                airportCode: value.split(' - ')[0] || ''
                            }
                        }
                    } else if (value && typeof value === 'object' && 'value' in value) {
                        // If value is an AirportInputState object, replace the entire field
                        return {
                            ...segment,
                            [field]: value as AirportInputState
                        }
                    }
                }
                return segment
            })
        )
    }

    /**
     * Swaps from and to for a specific segment
     * @param segmentIndex - Index of the segment to swap (0-indexed)
     */
    const handleMultiCitySwap = (segmentIndex: number) => {
        setMultiCitySegments(prev =>
            prev.map((segment, index) => {
                if (index === segmentIndex) {
                    return {
                        ...segment,
                        from: segment.to,
                        to: segment.from
                    }
                }
                return segment
            })
        )
    }

    /**
     * Adds a new segment (max segments based on maxSegments parameter)
     * If previous segment has a date, use it as default date for new segment (minDate)
     */
    const handleAddMultiCitySegment = () => {
        setMultiCitySegments(prev => {
            if (prev.length >= maxSegments) return prev

            // Get the most recent previous segment's date (minDate for new segment)
            let defaultDate: Date | null = null
            for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i]?.date) {
                    defaultDate = prev[i].date
                    break
                }
            }

            const newSegment: MultiCitySegment = {
                from: {
                    value: '',
                    city: '',
                    options: popularAirportList,
                    loading: false,
                    error: null,
                    airportCode: '',
                    isPopularAirports: true
                },
                to: {
                    value: '',
                    city: '',
                    options: popularAirportList,
                    loading: false,
                    error: null,
                    airportCode: '',
                    isPopularAirports: true
                },
                date: defaultDate // Use previous segment's date if available, otherwise null
            }
            return [...prev, newSegment]
        })
    }

    /**
     * Removes a segment at the given index (min 2 segments).
     * Any segment can be removed, including the first (index 0).
     * @param segmentIndex - Index of the segment to remove (0-indexed)
     */
    const handleRemoveMultiCitySegment = (segmentIndex?: number) => {
        setMultiCitySegments(prev => {
            if (prev.length <= 2) return prev
            // If no index provided, remove the last segment (backward compatibility)
            if (segmentIndex === undefined) {
                return prev.slice(0, -1)
            }
            return prev.filter((_, index) => index !== segmentIndex)
        })
    }

    /**
     * Fetches airport suggestions for a specific multi-city segment field
     * @param query - Search query string
     * @param segmentIndex - Index of the segment to update (0-indexed)
     * @param field - Whether searching for 'from' or 'to' airport
     */
    const getAirportsForMultiCitySegment = async (query: string, segmentIndex: number, field: 'from' | 'to') => {
        if (query.length < 1) {
            setMultiCitySegments(prev =>
                prev.map((segment, index) => {
                    if (index === segmentIndex) {
                        return {
                            ...segment,
                            [field]: {
                                ...segment[field],
                                options: popularAirportList,
                                loading: false,
                                isPopularAirports: true
                            }
                        }
                    }
                    return segment
                })
            )
            return
        }

        try {
            setMultiCitySegments(prev =>
                prev.map((segment, index) => {
                    if (index === segmentIndex) {
                        return {
                            ...segment,
                            [field]: {
                                ...segment[field],
                                loading: true,
                                error: null
                            }
                        }
                    }
                    return segment
                })
            )
            const airports = (await searchAirports(query)) as unknown as any
            setMultiCitySegments(prev =>
                prev.map((segment, index) => {
                    if (index === segmentIndex) {
                        return {
                            ...segment,
                            [field]: {
                                ...segment[field],
                                options: airports?.data || [],
                                loading: false,
                                isPopularAirports: false
                            }
                        }
                    }
                    return segment
                })
            )
        } catch (error) {
            console.error('Error fetching airports for multi-city segment:', error)
            setMultiCitySegments(prev =>
                prev.map((segment, index) => {
                    if (index === segmentIndex) {
                        return {
                            ...segment,
                            [field]: {
                                ...segment[field],
                                loading: false,
                                error: error instanceof Error ? error.message : 'An error occurred',
                                isPopularAirports: false
                            }
                        }
                    }
                    return segment
                })
            )
        }
    }

    const lastAIPreferences = useRef('')

    const handleAutoApplyPreferences = (lastInput: string) => {
        lastAIPreferences.current = lastInput
    }

    // ===== URL MANAGEMENT =====
    /**
     * Updates the browser URL with current search parameters
     * Formats dates and constructs query string for sharing/bookmarking
     */
    const router = useRouter()
    const updateSearchParams = () => {
        const queryParams = new URLSearchParams(window.location.search)
        queryParams.set('from', fromToInputsState.from.airportCode as string)
        queryParams.set('to', fromToInputsState.to.airportCode as string)
        queryParams.set('adults', travelersState.adults.toString())
        queryParams.set('childs', travelersState.childs.toString())
        queryParams.set('infants', travelersState.infants.toString())
        queryParams.set('class', flightClassState as string)
        queryParams.set('depart_date', getFormattedDate(range.from, 'DD/MM/YYYY'))
        queryParams.set('return_date', isRoundTrip ? getFormattedDate(range.to, 'DD/MM/YYYY') : '')
        queryParams.set('origin', fromToInputsState.from.value as string)
        queryParams.set('destination', fromToInputsState.to.value as string)
        queryParams.set('ai_filter_query', lastAIPreferences.current || '')
        const queryString = queryParams.toString()
        const path = isSeoQuery ? `/${locale}/flights/results` : window.location.pathname

        const url = `${path}?${queryString}`
        router.push(url)
    }

    useEffect(() => {
        if (popularAirportList.length > 0) {
            setFromToInputsState(prev => ({
                ...prev,
                from: {
                    ...prev.from,
                    options: popularAirportList
                },
                to: {
                    ...prev.to,
                    options: popularAirportList
                }
            }))
            setMultiCitySegments(prev =>
                prev.map(segment => ({
                    ...segment,
                    from: {
                        ...segment.from,
                        options: popularAirportList
                    },
                    to: {
                        ...segment.to,
                        options: popularAirportList
                    }
                }))
            )
        }
    }, [popularAirportList])

    // Return all state and handlers for component consumption
    return {
        // Airport selection state and handlers
        fromToInputsState,
        onAirportSelect,
        getAirportsFromQuery,
        handleSwap,

        // Trip type state and handlers
        isRoundTrip,
        isRoundTripDispatch,

        // Date range state and handlers
        range,
        rangeDispatch,

        // Traveler state and handlers
        travelersState,
        handleTravelerChange,

        // Flight class state and handlers
        flightClassState,
        handleFlightClassChange,

        // Business rule validation functions
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,

        // Non-stop flights state
        isNonStop,
        handleNonStopChange,

        // Multi-city state and handlers
        multiCitySegments,
        handleMultiCitySegmentUpdate,
        handleMultiCitySwap,
        handleAddMultiCitySegment,
        handleRemoveMultiCitySegment,
        getAirportsForMultiCitySegment,
        canAddMoreSegment: multiCitySegments.length < maxSegments,
        showRemoveSegment: multiCitySegments.length > 2, // Only show remove when there are 3+ segments (mobile: 3, desktop: 3-6)

        // URL management
        updateSearchParams,
        handleAutoApplyPreferences
    }
}
